import { useState, useRef, useEffect } from 'react'
import { Loader2, ChevronDown, Check, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import { useManagerSearch } from '@/hooks/useManagerSearch'
import { useSubmitForReview } from '@/hooks/useSubmitForReview'
import { toast } from '@/stores/useToastStore'
import type { UserRead } from '@/api/shares'

interface SubmitForReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  /** Replaces the assortment plan submission (e.g. wave plans). */
  onSubmit?: (payload: {
    notes: string | null
    reviewer_ids: string[]
  }) => Promise<unknown>
  reviewerRoles?: string[]
  title?: string
  description?: string
  submitLabel?: string
  /** Where focus goes on close (Radix ``onCloseAutoFocus``). */
  onCloseAutoFocus?: (event: Event) => void
}

// Generate initials from display_name or email
function getInitials(displayName: string | null, email: string | null): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return displayName.slice(0, 2).toUpperCase()
  }
  if (email) {
    return email.slice(0, 2).toUpperCase()
  }
  return '??'
}

// Avatar color combinations
const avatarColors = [
  { bg: 'bg-[#dbeafe]', text: 'text-[#155dfc]' },
  { bg: 'bg-[#dcfce7]', text: 'text-[#00a63e]' },
  { bg: 'bg-[#fef3c7]', text: 'text-[#d97706]' },
  { bg: 'bg-[#fce7f3]', text: 'text-[#db2777]' },
  { bg: 'bg-[#e0e7ff]', text: 'text-[#4f46e5]' },
]

function getAvatarColor(id: string | number) {
  const index =
    typeof id === 'string'
      ? id.charCodeAt(0) % avatarColors.length
      : id % avatarColors.length
  return avatarColors[index]
}

export function SubmitForReviewModal({
  open,
  onOpenChange,
  planId,
  onSubmit,
  reviewerRoles,
  title = 'Submit for Review',
  description = 'Select one or more reviewers and add a note to submit your plan.',
  submitLabel = 'Submit Plan',
  onCloseAutoFocus,
}: SubmitForReviewModalProps) {
  const [selectedReviewers, setSelectedReviewers] = useState<UserRead[]>([])
  const [notes, setNotes] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Hooks
  const { data: managers, isLoading: isLoadingManagers } = useManagerSearch({
    roles: reviewerRoles,
  })
  const submitMutation = useSubmitForReview(planId)
  const [customPending, setCustomPending] = useState(false)
  const isPending = onSubmit ? customPending : submitMutation.isPending

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggleReviewer = (manager: UserRead) => {
    setSelectedReviewers(prev =>
      prev.some(r => r.id === manager.id)
        ? prev.filter(r => r.id !== manager.id)
        : [...prev, manager]
    )
  }

  const handleRemoveReviewer = (managerId: UserRead['id']) => {
    setSelectedReviewers(prev => prev.filter(r => r.id !== managerId))
  }

  const handleSubmit = () => {
    const payload = {
      notes: notes.trim() || null,
      reviewer_ids: selectedReviewers.map(r => String(r.id)),
    }
    if (onSubmit) {
      // The caller's mutation reports success/errors (toasts).
      setCustomPending(true)
      onSubmit(payload)
        .then(() => handleOpenChange(false))
        .catch(() => undefined)
        .finally(() => setCustomPending(false))
      return
    }
    submitMutation.mutate(
      {
        notes: notes.trim() || null,
        reviewer_ids: selectedReviewers.map(r => String(r.id)),
      },
      {
        onSuccess: () => {
          toast.success('Plan submitted for review')
          handleOpenChange(false)
        },
        onError: (error: Error) => {
          toast.error(`Failed to submit: ${error.message}`)
        },
      }
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedReviewers([])
      setNotes('')
      setShowDropdown(false)
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="overflow-y-auto sm:max-w-[480px]"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Reviewer dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Reviewers</label>
            <div className="relative">
              <button
                ref={buttonRef}
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className={cn(
                  'flex min-h-9 w-full items-center justify-between gap-2',
                  'rounded-md border border-input bg-background px-3 py-2',
                  'text-sm ring-offset-background',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'cursor-pointer'
                )}
              >
                {selectedReviewers.length > 0 ? (
                  <div className="flex flex-1 flex-wrap gap-1">
                    {selectedReviewers.map(reviewer => (
                      <Badge
                        key={reviewer.id}
                        variant="secondary"
                        className="gap-1 py-1 pl-1 pr-1.5"
                      >
                        <Avatar className="h-4 w-4">
                          <AvatarFallback
                            className={cn(
                              getAvatarColor(reviewer.id).bg,
                              getAvatarColor(reviewer.id).text,
                              'text-[9px] font-medium'
                            )}
                          >
                            {getInitials(reviewer.display_name, reviewer.email)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="max-w-[140px] truncate">
                          {reviewer.display_name || reviewer.email}
                        </span>
                        <span
                          role="button"
                          tabIndex={-1}
                          aria-label={`Remove ${reviewer.display_name || reviewer.email}`}
                          className="ml-0.5 inline-flex cursor-pointer rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          onMouseDown={e => e.preventDefault()}
                          onClick={e => {
                            e.stopPropagation()
                            handleRemoveReviewer(reviewer.id)
                          }}
                        >
                          <X className="h-3 w-3" />
                        </span>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    Select reviewers
                  </span>
                )}
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>

              {/* Dropdown list */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
                >
                  {isLoadingManagers ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Loading reviewers...</span>
                    </div>
                  ) : managers && managers.length > 0 ? (
                    <ul className="py-1">
                      {managers.map(manager => {
                        const colors = getAvatarColor(manager.id)
                        const isSelected = selectedReviewers.some(
                          r => r.id === manager.id
                        )
                        return (
                          <li key={manager.id}>
                            <button
                              type="button"
                              onClick={() => handleToggleReviewer(manager)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150',
                                isSelected && 'bg-muted'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(
                                    manager.display_name,
                                    manager.email
                                  )}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {manager.display_name || manager.email}
                                </p>
                                {manager.display_name && manager.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {manager.email}
                                  </p>
                                )}
                              </div>
                              {isSelected && (
                                <Check className="h-4 w-4 text-primary" />
                              )}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">No reviewers available</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedReviewers.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Any selected reviewer can approve the plan.
              </p>
            )}
          </div>

          {/* Notes textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Add a note (optional)</label>
            <Textarea
              placeholder="Add any notes for the reviewers..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="min-h-[100px] resize-none"
              maxLength={2000}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || selectedReviewers.length === 0}
            className="gap-2 bg-[#CF1F2E] hover:bg-[#b81a27]"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
